"use client"

import { useEffect, useState } from "react"
import { useAuth } from "@/lib/auth/context"
import { createClient } from "@/lib/supabase/client"

/**
 * OneSignal Push Notification Provider
 * 
 * Uses the official OneSignal setup pattern with OneSignalDeferred.
 * OneSignal is initialized in app/layout.tsx via script tag and OneSignalDeferred.
 * This component sets up event listeners and manages subscription state.
 * 
 * IMPORTANT: This uses Custom Code Setup (not Auto Prompt).
 * Make sure Auto Prompt is DISABLED in OneSignal Dashboard:
 * Settings > Push & In-App > Web Settings > Permission Prompt Setup
 * 
 * See: https://documentation.onesignal.com/docs/en/permission-requests
 */
export function OneSignalProvider() {
  const { user } = useAuth()
  const [initialized, setInitialized] = useState(false)
  const [subscriptionStatus, setSubscriptionStatus] = useState<"subscribed" | "unsubscribed" | "unknown">("unknown")

  // Setup OneSignal event listeners after initialization
  const setupOneSignalListeners = (OneSignal: any) => {
    try {
      // Check subscription status
      const isSubscribed = OneSignal.User?.PushSubscription?.optedIn === true
      const playerId = OneSignal.User?.PushSubscription?.id
      const token = OneSignal.User?.PushSubscription?.token
      
      console.log("[OneSignal] Subscription status check:", {
        isSubscribed,
        playerId,
        hasToken: !!token,
        userId: user?.id
      })
      
      setSubscriptionStatus(isSubscribed ? "subscribed" : "unsubscribed")

      // If user is logged in and subscribed, save player ID to database
      if (user?.id && isSubscribed) {
        console.log("[OneSignal] User is subscribed, saving player ID...")
        // Save with retry in case player ID isn't immediately available
        const saveWithRetry = async () => {
          let retries = 0
          const maxRetries = 10
          
          while (retries < maxRetries) {
            const currentPlayerId = OneSignal.User?.PushSubscription?.id
            if (currentPlayerId) {
              await savePlayerId(OneSignal)
              break
            }
            console.log(`[OneSignal] Waiting for player ID in initial setup... (attempt ${retries + 1}/${maxRetries})`)
            await new Promise(resolve => setTimeout(resolve, 500))
            retries++
          }
        }
        saveWithRetry()
      } else if (user?.id && !isSubscribed) {
        console.log("[OneSignal] User is not subscribed yet")
      }

      // Listen for foreground notifications (when app is open)
      // This ensures notifications are displayed even when the app is in the foreground
      // IMPORTANT: Do NOT call preventDefault() - that would prevent the notification from showing
      // The notification will display automatically when the app is in foreground
      OneSignal.Notifications.addEventListener("foregroundWillDisplay", (event: any) => {
        console.log("[OneSignal] Foreground notification received - will display:", {
          title: event.notification?.title,
          body: event.notification?.body,
          data: event.notification?.additionalData,
          note: "Notification will be displayed automatically (do not call preventDefault)"
        })
        
        // The notification will be displayed automatically by OneSignal
        // We can customize it here if needed, but don't prevent it from showing
        // Example customization (optional):
        // event.notification.title = "Custom Title: " + event.notification.title
      })

      // Listen for notification clicks
      OneSignal.Notifications.addEventListener("click", (event: any) => {
        console.log("[OneSignal] Notification opened:", event)
        
        // Handle notification click - navigate to action URL if available
        const notification = event.notification
        if (notification?.additionalData?.action_url) {
          window.location.href = notification.additionalData.action_url
        } else if (notification?.launchURL) {
          window.location.href = notification.launchURL
        }
      })

      // Listen for subscription state changes
      OneSignal.User.PushSubscription.addEventListener("change", (change: any) => {
        const isSubscribed = change.current.optedIn === true
        const playerId = change.current.id
        console.log("[OneSignal] Subscription state changed:", {
          optedIn: isSubscribed,
          id: playerId,
          token: change.current.token ? "present" : "missing"
        })
        setSubscriptionStatus(isSubscribed ? "subscribed" : "unsubscribed")
        
        if (user?.id && isSubscribed) {
          // Save player ID with retry if not immediately available
          const saveWithRetry = async () => {
            let retries = 0
            const maxRetries = 10
            
            while (retries < maxRetries) {
              const currentPlayerId = OneSignal.User?.PushSubscription?.id
              if (currentPlayerId) {
                console.log("[OneSignal] Player ID available, saving...")
                await savePlayerId(OneSignal)
                break
              }
              console.log(`[OneSignal] Waiting for player ID... (attempt ${retries + 1}/${maxRetries})`)
              await new Promise(resolve => setTimeout(resolve, 500))
              retries++
            }
            
            if (retries >= maxRetries) {
              console.warn("[OneSignal] Player ID not available after retries")
            }
          }
          saveWithRetry()
        } else if (user?.id && !isSubscribed) {
          // Remove player ID when unsubscribed
          removePlayerId(OneSignal)
        }
      })

      // Listen for permission changes (important for iOS - permission state can reset)
      OneSignal.Notifications.addEventListener("permissionChange", (event: any) => {
        console.log("[OneSignal] Permission changed:", {
          permission: event.permission,
          current: event.current,
          previous: event.previous
        })
        
        // If permission was granted, ensure we're opted in
        if (event.permission === "granted") {
          const isOptedIn = OneSignal.User.PushSubscription.optedIn === true
          if (!isOptedIn) {
            console.log("[OneSignal] Permission granted but not opted in, opting in...")
            OneSignal.User.PushSubscription.optIn().then(() => {
              console.log("[OneSignal] Successfully opted in after permission change")
              setSubscriptionStatus("subscribed")
              if (user?.id) {
                // Save player ID with retry
                const saveWithRetry = async () => {
                  let retries = 0
                  const maxRetries = 10
                  
                  while (retries < maxRetries) {
                    const playerId = OneSignal.User?.PushSubscription?.id
                    if (playerId) {
                      await savePlayerId(OneSignal)
                      break
                    }
                    await new Promise(resolve => setTimeout(resolve, 500))
                    retries++
                  }
                }
                saveWithRetry()
              }
            }).catch((error: any) => {
              console.error("[OneSignal] Error opting in after permission change:", error)
            })
          }
        }
      })

      console.log("[OneSignal] Event listeners set up successfully")
    } catch (error) {
      console.error("[OneSignal] Error setting up listeners:", error)
    }
  }

  useEffect(() => {
    // Only initialize in browser environment
    if (typeof window === "undefined") return

    const appId = process.env.NEXT_PUBLIC_ONESIGNAL_APP_ID

    // Skip if OneSignal App ID is not configured
    if (!appId) {
      console.log("[OneSignal] App ID not configured. Push notifications disabled.")
      return
    }

    // Detect if running in Capacitor native app
    const isCapacitor = !!(window as any).Capacitor
    const isNative = isCapacitor && (window as any).Capacitor.isNativePlatform()
    
    if (isNative) {
      console.log("[OneSignal] Native push notifications are currently disabled for build compatibility.")
      return
    }

    // Skip OneSignal in development on localhost (domain mismatch with production config)
    // Only skip for web push, not for native (native doesn't have domain restrictions)
    if (process.env.NODE_ENV === "development" && window.location.hostname !== "www.spotstosurf.com") {
      console.log("[OneSignal] Disabled in development (localhost). Only works on production domain.")
      return
    }

    // Wait for OneSignal to be loaded via OneSignalDeferred pattern
    // The SDK script loads and initializes OneSignal, then calls our deferred functions
    const waitForOneSignal = async () => {
      // Wait for OneSignal to be available (loaded by script tag)
      let retries = 0
      const maxRetries = 50 // Wait up to 5 seconds (50 * 100ms)
      
      while (typeof (window as any).OneSignal === 'undefined' && retries < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, 100))
        retries++
      }

      const OneSignal = (window as any).OneSignal
      
      if (!OneSignal) {
        console.warn("[OneSignal] SDK not loaded after waiting. Check if script tag is present.")
        return
      }

      // Wait for initialization to complete (OneSignalDeferred calls init)
      let initRetries = 0
      while ((!OneSignal.initialized && !OneSignal._isInitialized) && initRetries < 30) {
        await new Promise(resolve => setTimeout(resolve, 100))
        initRetries++
      }

      if (!OneSignal.initialized && !OneSignal._isInitialized) {
        console.warn("[OneSignal] Initialization not complete after waiting")
        return
      }

      // Detect PWA mode for logging
      const isPWA = window.matchMedia("(display-mode: standalone)").matches ||
        (window.navigator as any).standalone === true
      
      console.log("[OneSignal] Ready, setting up listeners...", {
        isPWA,
        displayMode: window.matchMedia("(display-mode: standalone)").matches,
        navigatorStandalone: (window.navigator as any).standalone,
        hostname: window.location.hostname,
        OneSignalInitialized: OneSignal.initialized
      })

      setInitialized(true)
      setupOneSignalListeners(OneSignal)
      
      // Set external ID to link OneSignal user to our database user
      if (user?.id && OneSignal.User) {
        try {
          // Set external ID to the user's internal ID from our database
          // This makes it easier to find users in OneSignal dashboard
          const supabase = createClient()
          const { data: userProfile } = await supabase
            .from("users")
            .select("id")
            .eq("auth_id", user.id)
            .single()
          
          if (userProfile?.id) {
            await OneSignal.User.setExternalUserId(userProfile.id)
            console.log("[OneSignal] External ID set:", userProfile.id)
          }
        } catch (error) {
          console.warn("[OneSignal] Failed to set external ID:", error)
          // Don't fail initialization if external ID setting fails
        }
      }
    }

    waitForOneSignal()
  }, [user?.id])

  // Save player ID when user logs in or subscribes
  useEffect(() => {
    if (!initialized) return

    const OneSignal = (window as any).OneSignal
    if (!OneSignal) return

    const isSubscribed = OneSignal.User?.PushSubscription?.optedIn === true
    
    // Always try to save if user is logged in and subscribed
    // Don't wait for subscriptionStatus to match - it might be delayed
    if (user?.id && isSubscribed) {
      // Use a small delay to ensure player ID is available
      const saveWithRetry = async () => {
        let retries = 0
        const maxRetries = 5
        
        while (retries < maxRetries) {
          const playerId = OneSignal.User?.PushSubscription?.id
          if (playerId) {
            await savePlayerId(OneSignal)
            break
          }
          // Wait a bit for player ID to be generated
          await new Promise(resolve => setTimeout(resolve, 500))
          retries++
        }
      }
      saveWithRetry()
    } else if (!user?.id) {
      // Clear player ID when user logs out
      removePlayerId(OneSignal)
    }
  }, [user?.id, initialized, subscriptionStatus])

  /**
   * Save OneSignal player ID to database when user subscribes
   * Exported so it can be called from other components
   */
  const savePlayerId = async (OneSignal: any) => {
    if (!user?.id) {
      console.warn("[OneSignal] Cannot save player ID: user not logged in")
      return false
    }

    try {
      // For web push, the player ID is the subscription ID
      const playerId = OneSignal.User?.PushSubscription?.id
      const optedIn = OneSignal.User?.PushSubscription?.optedIn
      const token = OneSignal.User?.PushSubscription?.token
      
      console.log("[OneSignal] Attempting to save player ID...", {
        playerId,
        optedIn,
        hasToken: !!token,
        userId: user.id,
        subscriptionId: OneSignal.User?.PushSubscription?.id,
        subscriptionObject: OneSignal.User?.PushSubscription
      })

      if (!playerId) {
        console.warn("[OneSignal] ❌ No player ID available yet. Subscription may not be active.")
        console.warn("[OneSignal] Full PushSubscription object:", OneSignal.User?.PushSubscription)
        return false
      }

      if (!optedIn) {
        console.warn("[OneSignal] ❌ User not opted in, skipping player ID save")
        return false
      }

      const supabase = createClient()

      // Get user's internal ID from auth_id
      const { data: userProfile, error: profileError } = await supabase
        .from("users")
        .select("id")
        .eq("auth_id", user.id)
        .single()

      if (profileError || !userProfile?.id) {
        console.error("[OneSignal] ❌ User profile not found:", profileError)
        return false
      }

      console.log("[OneSignal] Found user profile:", userProfile.id)

      // Get current player IDs
      const { data: currentUser, error: fetchError } = await supabase
        .from("users")
        .select("onesignal_player_ids")
        .eq("id", userProfile.id)
        .single()

      if (fetchError) {
        console.error("[OneSignal] ❌ Error fetching current player IDs:", fetchError)
        return false
      }

      const existingPlayerIds = (currentUser?.onesignal_player_ids as string[]) || []
      
      console.log("[OneSignal] Current player IDs in database:", existingPlayerIds)
      
      // Add player ID if not already present
      if (!existingPlayerIds.includes(playerId)) {
        const updatedPlayerIds = [...existingPlayerIds, playerId]

        console.log("[OneSignal] Saving player ID to database...", {
          playerId,
          userId: userProfile.id,
          updatedPlayerIds
        })

        // Update by auth_id to match RLS policy (users_update_own requires auth.uid() = auth_id)
        const { error: updateError, data: updateData } = await supabase
          .from("users")
          .update({
            onesignal_player_ids: updatedPlayerIds,
          })
          .eq("auth_id", user.id)
          .select()

        if (updateError) {
          console.error("[OneSignal] ❌ Error saving player ID:", updateError)
          console.error("[OneSignal] Update error details:", JSON.stringify(updateError, null, 2))
          return false
        } else {
          console.log("[OneSignal] ✅ Player ID saved successfully!", {
            playerId,
            totalDevices: updatedPlayerIds.length,
            userId: userProfile.id,
            updatedRecord: updateData
          })
          return true
        }
        } else {
          console.log("[OneSignal] ✅ Player ID already exists in database:", {
            playerId,
            userId: userProfile.id,
            totalDevices: existingPlayerIds.length,
            allPlayerIds: existingPlayerIds
          })
          return true
        }
    } catch (error) {
      console.error("[OneSignal] ❌ Exception saving player ID:", error)
      console.error("[OneSignal] Error stack:", error instanceof Error ? error.stack : "No stack")
      return false
    }
  }

  /**
   * Remove OneSignal player ID when user unsubscribes or logs out
   */
  const removePlayerId = async (OneSignal: any) => {
    if (!user?.id) return

    try {
      // For web push, the player ID is the subscription ID
      const playerId = OneSignal.User?.PushSubscription?.id
      if (!playerId) return

      const supabase = createClient()

      // Get user's internal ID from auth_id
      const { data: userProfile } = await supabase
        .from("users")
        .select("id")
        .eq("auth_id", user.id)
        .single()

      if (!userProfile?.id) return

      // Remove player ID from array
      const { data: currentUser } = await supabase
        .from("users")
        .select("onesignal_player_ids")
        .eq("auth_id", user.id)
        .single()

      const existingPlayerIds = (currentUser?.onesignal_player_ids as string[]) || []
      const updatedPlayerIds = existingPlayerIds.filter((id) => id !== playerId)

      console.log("[OneSignal] Removing player ID from database...", {
        playerId,
        userId: userProfile.id,
        existingCount: existingPlayerIds.length,
        updatedCount: updatedPlayerIds.length
      })

      // Update by auth_id to match RLS policy (users_update_own requires auth.uid() = auth_id)
      const { error } = await supabase
        .from("users")
        .update({
          onesignal_player_ids: updatedPlayerIds,
        })
        .eq("auth_id", user.id)

      if (error) {
        console.error("[OneSignal] ❌ Error removing player ID:", error)
        console.error("[OneSignal] Remove error details:", JSON.stringify(error, null, 2))
      } else {
        console.log("[OneSignal] ✅ Player ID removed successfully!", {
          playerId,
          userId: userProfile.id,
          remainingDevices: updatedPlayerIds.length,
          wasRemoved: existingPlayerIds.length > updatedPlayerIds.length
        })
      }
    } catch (error) {
      console.error("[OneSignal] ❌ Exception removing player ID:", error)
      console.error("[OneSignal] Error stack:", error instanceof Error ? error.stack : "No stack")
    }
  }

  // This component doesn't render anything
  return null
}

/**
 * Request push notification permission
 * Call this function when user clicks a button to enable notifications
 * Uses the official OneSignal API from window.OneSignal
 */
export async function requestNotificationPermission(): Promise<boolean> {
  try {
    // Check if running in Capacitor native app
    const isCapacitor = typeof window !== 'undefined' && !!(window as any).Capacitor
    const isNative = isCapacitor && (window as any).Capacitor.isNativePlatform()

    if (isNative) {
      console.log("[OneSignal Native] Permission requests are currently disabled for build compatibility.")
      return false
    }

    // Check if OneSignal is available
    if (typeof window === 'undefined') {
      console.warn("[OneSignal Permission] window is undefined")
      return false
    }

    const OneSignal = (window as any).OneSignal
    const isPWA = window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as any).standalone === true
    
    console.log("[OneSignal Permission] Requesting permission...", {
      OneSignalExists: !!OneSignal,
      NotificationsExists: !!OneSignal?.Notifications,
      isPWA,
      displayMode: window.matchMedia("(display-mode: standalone)").matches,
      navigatorStandalone: (window.navigator as any).standalone,
      hostname: window.location.hostname
    })

    if (!OneSignal || !OneSignal.Notifications) {
      console.warn("[OneSignal Permission] OneSignal not available", {
        OneSignalExists: !!OneSignal,
        NotificationsExists: !!OneSignal?.Notifications,
        initialized: OneSignal?.initialized,
        _isInitialized: OneSignal?._isInitialized
      })
      throw new Error("OneSignal is not initialized. Push notifications may not be available.")
    }

    // Check current subscription status
    const currentStatus = OneSignal.User?.PushSubscription?.optedIn
    console.log("[OneSignal Permission] Current subscription status:", currentStatus)

    // Request permission first
    console.log("[OneSignal Permission] Calling requestPermission()...")
    const permission = await OneSignal.Notifications.requestPermission()
    console.log("[OneSignal Permission] Permission result:", permission, {
      type: typeof permission,
      value: permission
    })
    
    // If permission granted, opt in to push subscription
    if (permission) {
      console.log("[OneSignal Permission] Permission granted, opting in...")
      await OneSignal.User.PushSubscription.optIn()
      const newStatus = OneSignal.User?.PushSubscription?.optedIn
      let playerId = OneSignal.User?.PushSubscription?.id
      console.log("[OneSignal Permission] After optIn, subscription status:", {
        optedIn: newStatus,
        playerId,
        hasToken: !!OneSignal.User?.PushSubscription?.token
      })
      
      // Wait for player ID to be generated, then save it explicitly
      // The subscription change listener should also catch this, but we'll ensure it's saved here
      if (playerId) {
        console.log("[OneSignal Permission] Player ID available immediately, saving now...")
        // Call savePlayerIdToDatabase to ensure it's saved
        const saved = await savePlayerIdToDatabase()
        if (saved) {
          console.log("[OneSignal Permission] ✅ Player ID saved successfully")
        } else {
          console.warn("[OneSignal Permission] ⚠️ Player ID save failed, will retry via listener")
        }
      } else {
        console.log("[OneSignal Permission] Player ID not yet available, waiting...")
        // Wait up to 5 seconds for player ID
        let retries = 0
        const maxRetries = 10
        while (!playerId && retries < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 500))
          playerId = OneSignal.User?.PushSubscription?.id
          retries++
        }
        
        if (playerId) {
          console.log("[OneSignal Permission] Player ID generated, saving now...")
          await savePlayerIdToDatabase()
        } else {
          console.warn("[OneSignal Permission] Player ID not available after waiting, will be saved by listener")
        }
      }
      
      return true
    }
    
    console.log("[OneSignal Permission] Permission denied or not granted")
    return false
  } catch (error) {
    console.error("[OneSignal Permission] Error requesting permission:", error)
    throw error // Re-throw so caller can handle it
  }
}

/**
 * Check if push notifications are enabled
 * Uses the official OneSignal API from window.OneSignal
 */
export async function isPushNotificationsEnabled(): Promise<boolean> {
  try {
    const OneSignal = (window as any).OneSignal
    const isPWA = window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as any).standalone === true
    
    const playerId = OneSignal?.User?.PushSubscription?.id
    const token = OneSignal?.User?.PushSubscription?.token
    const optedIn = OneSignal?.User?.PushSubscription?.optedIn
    
    console.log("[OneSignal Status] Checking notification status...", {
      OneSignalExists: !!OneSignal,
      UserExists: !!OneSignal?.User,
      PushSubscriptionExists: !!OneSignal?.User?.PushSubscription,
      optedIn,
      playerId,
      hasToken: !!token,
      isPWA,
      hostname: window.location.hostname,
      initialized: OneSignal?.initialized
    })

    if (!OneSignal || !OneSignal.User?.PushSubscription) {
      console.warn("[OneSignal Status] OneSignal or PushSubscription not available")
      return false
    }

    // Check if user has opted in to push subscription
    const isSubscribed = optedIn === true
    console.log("[OneSignal Status] Subscription status:", {
      isSubscribed,
      playerId,
      hasToken: !!token
    })
    return isSubscribed
  } catch (error) {
    console.error("[OneSignal Status] Error checking push notification status:", error)
    return false
  }
}

/**
 * Get detailed OneSignal status for debugging
 * Call this from browser console: window.getOneSignalStatus()
 */
export function getOneSignalStatus() {
  if (typeof window === 'undefined') {
    return { error: "Not in browser environment" }
  }

  const OneSignal = (window as any).OneSignal
  const isPWA = window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as any).standalone === true

  if (!OneSignal) {
    return {
      error: "OneSignal not loaded",
      suggestion: "Check if script tag is present in page source"
    }
  }

  const subscription = OneSignal.User?.PushSubscription
  const playerId = subscription?.id
  const token = subscription?.token
  const optedIn = subscription?.optedIn

  return {
    initialized: OneSignal.initialized || OneSignal._isInitialized,
    isPWA,
    hostname: window.location.hostname,
    subscription: {
      optedIn,
      playerId,
      hasToken: !!token,
      tokenLength: token?.length || 0
    },
    user: {
      id: OneSignal.User?.id,
      email: OneSignal.User?.email,
      externalId: OneSignal.User?.externalId
    },
    notifications: {
      permission: OneSignal.Notifications?.permissionNative || "unknown"
    }
  }
}

/**
 * Manually save player ID to database (for debugging)
 * Call this from browser console: window.forceSavePlayerId()
 */
export async function forceSavePlayerId(): Promise<boolean> {
  if (typeof window === 'undefined') {
    console.error("Not in browser environment")
    return false
  }

  const OneSignal = (window as any).OneSignal
  if (!OneSignal) {
    console.error("OneSignal not available")
    return false
  }

  const playerId = OneSignal.User?.PushSubscription?.id
  const optedIn = OneSignal.User?.PushSubscription?.optedIn

  console.log("[OneSignal Force Save] Current status:", {
    playerId,
    optedIn,
    hasToken: !!OneSignal.User?.PushSubscription?.token
  })

  if (!playerId) {
    console.error("[OneSignal Force Save] No player ID available")
    return false
  }

  if (!optedIn) {
    console.error("[OneSignal Force Save] User not opted in")
    return false
  }

  // Get user from auth context (we'll need to import it or get it another way)
  // For now, we'll use the Supabase client directly
  const { createClient } = await import("@/lib/supabase/client")
  const supabase = createClient()

  // Get current user
  const { data: { user }, error: authError } = await supabase.auth.getUser()
  if (authError || !user) {
    console.error("[OneSignal Force Save] User not authenticated:", authError)
    return false
  }

  // Get user's internal ID
  const { data: userProfile, error: profileError } = await supabase
    .from("users")
    .select("id")
    .eq("auth_id", user.id)
    .single()

  if (profileError || !userProfile?.id) {
    console.error("[OneSignal Force Save] User profile not found:", profileError)
    return false
  }

  // Get current player IDs
  const { data: currentUser } = await supabase
    .from("users")
    .select("onesignal_player_ids")
    .eq("id", userProfile.id)
    .single()

  const existingPlayerIds = (currentUser?.onesignal_player_ids as string[]) || []
  const updatedPlayerIds = existingPlayerIds.includes(playerId) 
    ? existingPlayerIds 
    : [...existingPlayerIds, playerId]

  const { error: updateError, data: updateData } = await supabase
    .from("users")
    .update({
      onesignal_player_ids: updatedPlayerIds,
      updated_at: new Date().toISOString(),
    })
    .eq("id", userProfile.id)
    .select()

  if (updateError) {
    console.error("[OneSignal Force Save] Error:", updateError)
    return false
  }

  console.log("[OneSignal Force Save] ✅ Success!", {
    playerId,
    updatedRecord: updateData
  })
  return true
}

/**
 * Export savePlayerId function so it can be called from other components
 * This ensures player ID is saved immediately when user toggles notifications on
 */
export async function savePlayerIdToDatabase(): Promise<boolean> {
  if (typeof window === 'undefined') {
    console.warn("[OneSignal] Cannot save player ID: not in browser")
    return false
  }

  const OneSignal = (window as any).OneSignal
  if (!OneSignal) {
    console.warn("[OneSignal] Cannot save player ID: OneSignal not available")
    return false
  }

  // Get user from Supabase auth
  const { createClient } = await import("@/lib/supabase/client")
  const supabase = createClient()
  const { data: { user }, error: authError } = await supabase.auth.getUser()
  
  if (authError || !user) {
    console.warn("[OneSignal] Cannot save player ID: user not authenticated", authError)
    return false
  }

  const playerId = OneSignal.User?.PushSubscription?.id
  const optedIn = OneSignal.User?.PushSubscription?.optedIn

  console.log("[OneSignal savePlayerIdToDatabase] Current status:", {
    playerId,
    optedIn,
    hasToken: !!OneSignal.User?.PushSubscription?.token,
    userId: user.id
  })

  if (!playerId) {
    console.warn("[OneSignal] Cannot save player ID: no player ID available")
    return false
  }

  if (!optedIn) {
    console.warn("[OneSignal] Cannot save player ID: user not opted in")
    return false
  }

  // Get user's internal ID
  const { data: userProfile, error: profileError } = await supabase
    .from("users")
    .select("id")
    .eq("auth_id", user.id)
    .single()

  if (profileError || !userProfile?.id) {
    console.error("[OneSignal] User profile not found:", profileError)
    return false
  }

  // Get current player IDs
  const { data: currentUser } = await supabase
    .from("users")
    .select("onesignal_player_ids")
    .eq("id", userProfile.id)
    .single()

  const existingPlayerIds = (currentUser?.onesignal_player_ids as string[]) || []
  
  if (existingPlayerIds.includes(playerId)) {
    console.log("[OneSignal] ✅ Player ID already saved:", playerId)
    return true
  }

  const updatedPlayerIds = [...existingPlayerIds, playerId]

  console.log("[OneSignal] Saving player ID to database...", {
    playerId,
    userId: userProfile.id,
    authId: user.id,
    existingCount: existingPlayerIds.length,
    updatedCount: updatedPlayerIds.length
  })

  // Update by auth_id to match RLS policy (users_update_own requires auth.uid() = auth_id)
  const { error: updateError, data: updateData } = await supabase
    .from("users")
    .update({
      onesignal_player_ids: updatedPlayerIds,
    })
    .eq("auth_id", user.id)
    .select()

  if (updateError) {
    console.error("[OneSignal] ❌ Error saving player ID:", updateError)
    console.error("[OneSignal] Update error details:", JSON.stringify(updateError, null, 2))
    return false
  }

  console.log("[OneSignal] ✅ Player ID saved successfully!", {
    playerId,
    userId: userProfile.id,
    updatedRecord: updateData
  })
  return true
}

/**
 * Diagnostic function to check player ID status
 * Call from browser console: window.checkPlayerIdStatus()
 */
export async function checkPlayerIdStatus(): Promise<void> {
  if (typeof window === 'undefined') {
    console.warn("[OneSignal Diagnostic] Cannot check: not in browser")
    return
  }

  const OneSignal = (window as any).OneSignal
  if (!OneSignal) {
    console.warn("[OneSignal Diagnostic] OneSignal not available")
    return
  }

  // Get user from Supabase auth
  const { createClient } = await import("@/lib/supabase/client")
  const supabase = createClient()
  const { data: { user }, error: authError } = await supabase.auth.getUser()
  
  if (authError || !user) {
    console.warn("[OneSignal Diagnostic] User not authenticated")
    return
  }

  const playerId = OneSignal.User?.PushSubscription?.id
  const optedIn = OneSignal.User?.PushSubscription?.optedIn
  const token = OneSignal.User?.PushSubscription?.token

  console.log("[OneSignal Diagnostic] Current OneSignal Status:", {
    playerId,
    optedIn,
    hasToken: !!token,
    userId: user.id,
    initialized: OneSignal.initialized
  })

  // Check database status
  try {
    const response = await fetch("/api/onesignal/check-player-id")
    const data = await response.json()
    
    console.log("[OneSignal Diagnostic] Database Status:", data)
    
    if (data.success) {
      if (data.onesignal.hasPlayerIds) {
        console.log("✅ Player ID is registered in database")
        console.log("   Player IDs:", data.onesignal.playerIds)
      } else {
        console.warn("⚠️ Player ID is NOT registered in database")
        console.warn("   Solution: Enable notifications and ensure player ID is saved")
      }
      
      if (data.notifications.chatMessagesEnabled) {
        console.log("✅ Chat notifications are enabled in preferences")
      } else {
        console.warn("⚠️ Chat notifications are disabled in preferences")
      }
    }
  } catch (error) {
    console.error("[OneSignal Diagnostic] Error checking database:", error)
  }
}

// Expose debug functions to window for easy console access
if (typeof window !== 'undefined') {
  (window as any).getOneSignalStatus = getOneSignalStatus
  ;(window as any).forceSavePlayerId = forceSavePlayerId
  ;(window as any).savePlayerIdToDatabase = savePlayerIdToDatabase
  ;(window as any).checkPlayerIdStatus = checkPlayerIdStatus
}
